import { computed, effect, inject } from '@angular/core';
import { TransactionStore } from '@app/transaction/store/transaction.store';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { WaterTransferAssetType } from '@ui/water/enums/water-transfer-asset-type.enum';
import { firstValueFrom } from 'rxjs';
import { WaterTransferDocumentServiceService } from '../services/water-transfer-document-service.service';
import { WaterServiceRequest } from '../types/water-transfer-document-service-dto';
import { WaterTransferDocumentService } from '../classes/water-transfer-document-service';

type WaterTransferDocumentState = {
    isLoading: boolean;
    waterTransferDocumentService: WaterTransferDocumentService;
};

const defaultState: WaterTransferDocumentState = {
    isLoading: false,
    waterTransferDocumentService: null,
};

export const WaterTransferDocumentServiceStore = signalStore(
    { providedIn: 'root' },
    withState(defaultState),
    withComputed((state) => {
        const serviceActive = () => !!state.waterTransferDocumentService()?.active;

        const getAssetWaterTransferDocument = () =>
            state
                .waterTransferDocumentService()
                ?.waterTransferAssets?.find((asset) => asset.waterTransferAssetType === WaterTransferAssetType.TransferDocument);

        return {
            active: computed(() => serviceActive()),
            supplierComplete: computed(() => !!state.waterTransferDocumentService()?.supplier),
            supplier: computed(() => state.waterTransferDocumentService()?.supplier),
            assets: computed(() => state.waterTransferDocumentService()?.waterTransferAssets),
            hasAssetWaterTransferDocument: computed(() => !!getAssetWaterTransferDocument()),
            getStatus: computed(() => state.waterTransferDocumentService()?.status),
            newResidentsInfoComplete: computed(() => !!state.waterTransferDocumentService()?.newResidentsInfoComplete()),
            meterComplete: computed(() => state.waterTransferDocumentService()?.states?.meterComplete),
        };
    }),
    withMethods((store, waterTransferDocumentServiceService = inject(WaterTransferDocumentServiceService)) => ({
        set(state: Partial<WaterTransferDocumentState>) {
            patchState(store, state);
        },
        reset() {
            patchState(store, defaultState);
        },
        async load(transactionId: string) {
            patchState(store, { isLoading: true });
            try {
                const water = await firstValueFrom(waterTransferDocumentServiceService.getWaterService(transactionId));
                patchState(store, {
                    isLoading: false,
                    waterTransferDocumentService: new WaterTransferDocumentService(water),
                });
            } catch (error) {
                patchState(store, { isLoading: false });
            }
        },
        async patch(transactionId: string, payload: WaterServiceRequest) {
            patchState(store, { isLoading: true });
            try {
                const water = await firstValueFrom(waterTransferDocumentServiceService.patchWaterService(transactionId, payload));
                patchState(store, {
                    isLoading: false,
                    waterTransferDocumentService: new WaterTransferDocumentService(water),
                });
            } catch (error) {
                patchState(store, { isLoading: false });
            }
        },
        async setSubmitted(transactionId: string, submitted: boolean) {
            patchState(store, { isLoading: true });
            try {
                const updated = await firstValueFrom(waterTransferDocumentServiceService.submitWaterService(transactionId, submitted));
                patchState(store, {
                    isLoading: false,
                    waterTransferDocumentService: new WaterTransferDocumentService(updated),
                });
            } catch (error) {
                patchState(store, {
                    isLoading: false,
                });
            }
        },
        async setActive(transactionId: string, active: boolean) {
            patchState(store, { isLoading: true });
            try {
                const updated = await firstValueFrom(waterTransferDocumentServiceService.setActive(transactionId, active));
                patchState(store, {
                    isLoading: false,
                    waterTransferDocumentService: new WaterTransferDocumentService(updated),
                });
            } catch (error) {
                patchState(store, {
                    isLoading: false,
                });
            }
        },
        async updateWaterTransferAssets(
            transactionId: string,
            assetKeysToRemove: string[],
            files: File[],
            waterTransferAssetType?: WaterTransferAssetType,
        ) {
            patchState(store, { isLoading: true });
            try {
                const updated = await firstValueFrom(
                    waterTransferDocumentServiceService.updateWaterTransferAssets(
                        transactionId,
                        assetKeysToRemove,
                        files,
                        waterTransferAssetType,
                    ),
                );
                patchState(store, {
                    isLoading: false,
                    waterTransferDocumentService: new WaterTransferDocumentService(updated),
                });
            } catch (error) {
                patchState(store, {
                    isLoading: false,
                });
            }
        },
        async generateWaterTransferDocument(transactionId: string) {
            patchState(store, { isLoading: true });
            try {
                const updated = await firstValueFrom(waterTransferDocumentServiceService.generateWaterTransferDocument(transactionId));
                patchState(store, {
                    isLoading: false,
                    waterTransferDocumentService: new WaterTransferDocumentService(updated),
                });
                return updated;
            } catch (error) {
                patchState(store, { isLoading: false });
                throw error;
            }
        },
    })),
    withHooks(({ load }) => {
        const transactionStore = inject(TransactionStore);

        effect(
            () => {
                if (transactionStore.transactionId()) {
                    load(transactionStore.transactionId());
                }
            },
            { allowSignalWrites: true },
        );

        return {
            onInit() {},
        };
    }),
);
